import { supabase } from '@/lib/supabase';
import { Listing } from '@/components/JobCard';
import { Contact, claimLead } from '@/lib/marketplace';
import { logPiiAccess, CONTACT_CARD_FIELDS } from '@/lib/piiAccess';
import { creditsChanged } from '@/lib/creditsBus';
import { captureError } from '@/lib/monitoring';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * MY PURCHASED LEADS — one data layer for every surface that shows them
 * (the dashboard preview card and the dedicated /my-leads page).
 * ───────────────────────────────────────────────────────────────────────────
 *
 * PERMANENT ACCESS: every read below is authorised by the purchase row itself.
 *   mkt_lead_purchases     buyer_id = caller            (p_purch_read)
 *   mkt_listings           mkt_bought_listing(id)       (p_listings_read)
 *   mkt_listing_contacts   EXISTS purchase for caller   (p_contacts_read)
 * None of them depend on the listing still being open, so a lead that later
 * sells out, closes or expires stays readable to the contractor who paid.
 */

export const LEAD_STAGES = ['purchased', 'contacted', 'estimate', 'won', 'lost'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

export interface PurchasedLead {
  id: string;
  listingId: string;
  amount: number;
  stage: string;
  method: string;
  createdAt: string;
  listing: Listing | null;
  contact: Contact | null;
}

/** A payment that is confirmed for a specific lead but not yet unlocked. */
export interface PendingPaidLead {
  key: string;
  listingId: string;
  slug: string | null;
  title: string | null;
  status: string;
  paidAt: string;
}

export interface HistoryRow {
  id: string;
  kind: string;
  description: string | null;
  amount: number;
  status: string;
  createdAt: string;
}

/** All purchased leads with listing + contact, newest first. */
export const fetchPurchasedLeads = async (userId: string): Promise<PurchasedLead[]> => {
  const { data, error } = await supabase
    .from('mkt_lead_purchases')
    .select('id, listing_id, amount, stage, purchase_method, created_at')
    .eq('buyer_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = (data || []) as Array<{
    id: string;
    listing_id: string;
    amount: number;
    stage: string;
    purchase_method: string;
    created_at: string;
  }>;
  if (!rows.length) return [];

  const ids = rows.map((r) => r.listing_id).filter(Boolean);
  const [{ data: ls }, { data: cs }] = await Promise.all([
    supabase.from('mkt_listings').select('*').in('id', ids),
    supabase.from('mkt_listing_contacts').select('*').in('listing_id', ids),
  ]);
  const lmap: Record<string, Listing> = {};
  (ls || []).forEach((l: Listing) => (lmap[l.id] = l));
  const cmap: Record<string, Contact> = {};
  (cs || []).forEach((c: Contact) => (cmap[c.listing_id] = c));

  // Audit trail: reading contact rows is a PII access (field names only).
  Object.keys(cmap).forEach((listingId) =>
    void logPiiAccess({ listingId, surface: 'purchased_contact_card', fields: CONTACT_CARD_FIELDS }),
  );

  return rows.map((r) => ({
    id: r.id,
    listingId: r.listing_id,
    amount: r.amount,
    stage: r.stage,
    method: r.purchase_method,
    createdAt: r.created_at,
    listing: lmap[r.listing_id] || null,
    contact: cmap[r.listing_id] || null,
  }));
};

/**
 * Pipeline stage is the ONLY field a buyer may change on a purchase, and only
 * through this RPC (own row, allowed stage values). Direct table updates are
 * blocked by RLS so amount / method / payment reference cannot be rewritten.
 *
 * MONITORING (2026-10-14): every call the server receives is logged inside the
 * RPC (mkt_rpc_outcomes). Failures where the request never reached the RPC
 * (network, permission, missing function) are reported here with the
 * `rpc_failed:` prefix so alert-watch check 7c can alert on them.
 */
export const updateLeadStage = async (purchaseId: string, stage: string) => {
  const { data, error } = await supabase.rpc('mkt_update_lead_stage', { p_purchase_id: purchaseId, p_stage: stage });
  if (error) {
    captureError({
      message: `rpc_failed:mkt_update_lead_stage: ${error.message || 'request failed'}`,
      kind: 'edge',
      area: 'lead_stage_rpc',
      context: { rpc: 'mkt_update_lead_stage', code: (error as { code?: string }).code ?? null, stage },
    });
    throw error;
  }
  const res = (data || {}) as { ok?: boolean; error?: string };
  if (!res.ok) throw new Error(res.error || 'stage_update_failed');
};

/**
 * PAID BUT NOT YET UNLOCKED.
 * The hosted $29 checkout credits the account via the payment webhook, but the
 * unlock itself used to wait for the buyer to come back to the project page and
 * tap Unlock. If the page closed, the money became a banked credit and the lead
 * appeared nowhere. These are the confirmed-paid, lead-specific payments that
 * have no matching purchase yet.
 */
export const fetchPendingPaidLeads = async (
  userId: string,
  ownedListingIds: string[],
): Promise<PendingPaidLead[]> => {
  const owned = new Set(ownedListingIds);
  const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();

  const [{ data: sessions }, { data: orders }] = await Promise.all([
    supabase
      .from('mkt_hosted_checkout_sessions')
      .select('id, listing_id, listing_slug, status, credited_at, started_at')
      .eq('user_id', userId)
      .eq('plan_code', 'single')
      .eq('status', 'credited')
      .gte('started_at', since),
    supabase
      .from('mkt_lead_credit_orders')
      .select('id, listing_id, status, credited_at, created_at, purchase_id')
      .eq('user_id', userId)
      .in('status', ['paid', 'credited'])
      .is('purchase_id', null)
      .gte('created_at', since),
  ]);

  const out: PendingPaidLead[] = [];
  const seen = new Set<string>();
  (sessions || []).forEach((s: any) => {
    if (!s.listing_id || owned.has(s.listing_id) || seen.has(s.listing_id)) return;
    seen.add(s.listing_id);
    out.push({
      key: `hcs:${s.id}`,
      listingId: s.listing_id,
      slug: s.listing_slug || null,
      title: null,
      status: s.status,
      paidAt: s.credited_at || s.started_at,
    });
  });
  (orders || []).forEach((o: any) => {
    if (!o.listing_id || owned.has(o.listing_id) || seen.has(o.listing_id)) return;
    seen.add(o.listing_id);
    out.push({
      key: `lco:${o.id}`,
      listingId: o.listing_id,
      slug: null,
      title: null,
      status: o.status,
      paidAt: o.credited_at || o.created_at,
    });
  });
  return out;
};

const RESUME_MARK = 'gg_paid_lead_resume';
const attempted = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(RESUME_MARK) || '{}');
  } catch {
    return {};
  }
};
const markAttempt = (key: string) => {
  try {
    const m = attempted();
    m[key] = Date.now();
    localStorage.setItem(RESUME_MARK, JSON.stringify(m));
  } catch {
    /* ignore */
  }
};

/**
 * Finish the unlock for a confirmed-paid lead. Safe to repeat: mkt_claim_lead
 * short-circuits on an existing entitlement and never spends a second credit.
 */
export const unlockPaidLead = async (listingId: string) => {
  const res = await claimLead(listingId);
  if (res.ok || res.reason === 'already_owned' || res.reason === 'already_purchased') creditsChanged();
  return res;
};

/**
 * AUTOMATIC RESUME — called whenever the contractor opens the dashboard or My
 * Leads. Each pending paid lead is attempted once per 10 minutes per browser,
 * so a sold-out lead (credit stays banked) never loops.
 * Returns how many leads were unlocked.
 */
export const resumePaidLeads = async (pending: PendingPaidLead[]): Promise<number> => {
  const marks = attempted();
  let unlocked = 0;
  for (const p of pending) {
    if (marks[p.key] && Date.now() - marks[p.key] < 10 * 60 * 1000) continue;
    markAttempt(p.key);
    try {
      const r = await unlockPaidLead(p.listingId);
      if (r.ok) unlocked += 1;
    } catch {
      /* the row stays visible with a manual Unlock button */
    }
  }
  return unlocked;
};

/** Payment history (leads, memberships, credit packs) for this account. */
export const fetchPurchaseHistory = async (userId: string): Promise<HistoryRow[]> => {
  const { data } = await supabase
    .from('mkt_payments')
    .select('id, kind, description, amount, status, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(100);
  return ((data || []) as any[]).map((r) => ({
    id: r.id,
    kind: r.kind,
    description: r.description,
    amount: r.amount,
    status: r.status,
    createdAt: r.created_at,
  }));
};

export const METHOD_LABEL: Record<string, { en: string; es: string }> = {
  single_29: { en: 'Single lead', es: 'Lead individual' },
  membership_credit: { en: 'Membership credit', es: 'Crédito de membresía' },
  credit_pack: { en: 'Lead credit', es: 'Crédito de lead' },
  admin_grant: { en: 'Courtesy credit', es: 'Crédito de cortesía' },
  unknown: { en: 'Lead credit', es: 'Crédito de lead' },
};

export const formatLeadDate = (iso: string, lang: string) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};
